import { FacebookCustomEvent } from '../../analytics/facebook';
import { FirebaseShareEvent } from '../../analytics/firebase';
import { nativeLogCampaign } from '../../analytics/nativeFirebase';
import config from '../../config';
import { authInfo } from '../../ducks/auth.duck';
import { stashAuthLinkParams } from '../authLink';
import applyConsent from '../consent/applyConsent';
import { captureAttributionFromUrl } from '../consent/attribution';
import { getConsent } from '../consent/consentState';
import { checkNativeAppUpdate } from './nativeAppUpdate';
import { handleBackButton } from './backButton';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { getClientData } from '../clientData';
import { getLayoutWidth } from '../layoutWidth';
import { logError, logWarning } from '../log';
import { resolveAppLocale } from './appLocale';
import { oneSignalInit, syncConsentTags } from './oneSignal';

// TODO Change to use MUI breakpoints
export const MODAL_BREAKPOINT = 900;

// TODO: isMobileSize replace with useMediaQuery
export const isMobileSize = () =>
  typeof window !== 'undefined' && getLayoutWidth() < MODAL_BREAKPOINT;

/**
 * Formats a DOMRect-like rect as the "x,y,w,h" string the share plugin reads from `iPadCoordinates`.
 * Without it the iPad popover is anchored at the top-left corner of the screen.
 */
export const toIPadCoordinates = (rect) =>
  rect
    ? [rect.x, rect.y, rect.width, rect.height].map((value) => Math.round(value)).join(',')
    : undefined;

export const openDeviceShareDialog = (dialogOptions, listingId, currentUser, anchorRect = null) => {
  const iPadCoordinates = toIPadCoordinates(anchorRect);
  window.plugins.socialsharing.shareWithOptions(
    iPadCoordinates ? { ...dialogOptions, iPadCoordinates } : dialogOptions,
    (result) => {
      FirebaseShareEvent.withContentType('listing')
        .withItemId(listingId)
        .withMethod('deviceShareDialog')
        .logEvent();
      FacebookCustomEvent.withCurrentUserUUID(currentUser?.id?.uuid)
        .withCustomName('deviceShareDialog')
        .withParams({ listingId })
        .logEvent();
      console.log('[socialsharing] shared to app', result);
    },
    (error) => {
      console.log('[socialsharing] sharing failed with message', JSON.stringify(error));
    },
  );
};

const EXCLUDED_PATHS = ['/api/newsletter/confirm'];

// Ensure path has a locale prefix — external links (e.g. Sharetribe password
// reset emails) use bare paths like /reset-password which the RR7 data router
// misinterprets as a locale param, landing the user on the homepage instead.
const withLocalePrefix = (path) => {
  const firstSegment = path.replace(/^\//, '').split('/')[0];
  const supportedLocales = Object.keys(config.languages || {});
  return firstSegment && !supportedLocales.includes(firstSegment)
    ? `/${resolveAppLocale()}${path}`
    : path;
};

const toSearch = (queryString) => {
  if (!queryString) return '';
  return queryString.startsWith('?') ? queryString : '?' + queryString;
};

// The router mounts after deviceready, so a cold-start deep link may arrive first.
const navigateWhenReady = (location) => {
  if (window.routerNavigate) {
    window.routerNavigate(location);
    return;
  }
  let attempts = 0;
  const maxAttempts = 100;
  const wait = setInterval(() => {
    attempts++;
    if (window.routerNavigate) {
      clearInterval(wait);
      console.log('[deeplink] routerNavigate ready, navigating');
      window.routerNavigate(location);
    } else if (attempts >= maxAttempts) {
      clearInterval(wait);
      console.error('[deeplink] routerNavigate not available after 10s, giving up');
    }
  }, 100);
};

// Campaign links (newsletter, OneSignal) open the app with utm_* in the URL.
// The app's hash router keeps them out of window.location.search, where the
// boot-time capture in app.js looks, so they are picked up here.
const captureDeepLinkCampaign = (search, url) => {
  try {
    nativeLogCampaign(captureAttributionFromUrl(search), url);
  } catch (e) {
    // Handled, not critical: the link still opens, only its campaign is lost
    // (e.g. a corrupt stored attribution or a full storage quota).
    logWarning(e, 'deeplink-campaign-capture-failed');
  }
};

const initDeeplink = () => {
  window.IonicDeeplink.onDeepLink(function(data) {
    try {
      if (EXCLUDED_PATHS.some(excluded => data.path?.startsWith(excluded))) {
        console.log('[deeplink] excluded path, ignoring:', data.path);
        return;
      }

      console.log('[deeplink] received', JSON.stringify(data));

      let path = data.path;
      if (!path && data.url) {
        try {
          const parsed = new URL(data.url);
          path = parsed.pathname;
          console.log('[deeplink] parsed path from url:', path);
        } catch (urlError) {
          console.error('[deeplink] failed to parse url:', data.url, urlError);
        }
      }

      if (path) {
        const prefixed = withLocalePrefix(path);
        if (prefixed !== path) console.log('[deeplink] prepended locale:', prefixed);
        path = prefixed;
      }

      const pathname = path || '/';
      const search = toSearch(data.queryString);
      captureDeepLinkCampaign(search, data.url);
      const deepLocation = {
        pathname,
        search: stashAuthLinkParams(pathname, search),
      };

      console.log('[deeplink] navigating to:', JSON.stringify(deepLocation));
      navigateWhenReady(deepLocation);
    } catch (error) {
      console.error('[deeplink] error handling deep link:', error);
    }
  });
};

// index.js awaitet diese Promise VOR root.render() — sie darf deshalb unter
// KEINEN Umständen hängen bleiben, sonst mountet React nie (Whitescreen).
export const DEVICEREADY_WATCHDOG_MS = 10000;

// Momentaufnahme dessen, was Cordova bereits bereitgestellt hat. Der
// Watchdog-Report meldete bisher nur "nicht innerhalb von 10s" und ließ damit
// offen, ob ein Plugin hängt oder der native Boot bloß langsam ist
// (VANSITE-FRONTEND-4MD, bislang ausschließlich iOS).
const cordovaBootSnapshot = () => ({
  platformId: window.cordova?.platformId ?? null,
  // Das device-Plugin registriert sich als eines der ersten — fehlt es, steckt
  // der native Boot noch ganz am Anfang.
  hasDevice: !!window.device,
  // Genau die Namespaces, die der deviceready-Handler unten anfasst.
  availablePlugins: [
    ['StatusBar', !!window.StatusBar],
    ['InAppBrowser', !!window.cordova?.InAppBrowser],
    ['getAppVersion', typeof window.cordova?.getAppVersion === 'function'],
    ['cordova.plugins', !!window.cordova?.plugins],
    ['plugins', !!window.plugins],
  ]
    .filter(([, present]) => present)
    .map(([name]) => name),
});

export const initCordovaScripts = (dispatch) => {
  return new Promise((resolve) => {
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      resolve({});
    };

    const bootStartedAt = Date.now();
    let watchdogFired = false;

    // Feuert deviceready nie (Plugin-/Native-Fehler), rendern wir trotzdem —
    // App ohne Push/Deeplink ist besser als Whitescreen.
    const watchdog = setTimeout(() => {
      watchdogFired = true;
      // Handled, not critical: the watchdog renders anyway, so the app
      // doesn't stay on a whitescreen (VANSITE-FRONTEND-4MD).
      logWarning(
        new Error(`deviceready not fired within ${DEVICEREADY_WATCHDOG_MS}ms — rendering anyway`),
        'cordova-boot-watchdog',
        cordovaBootSnapshot(),
      );
      settle();
    }, DEVICEREADY_WATCHDOG_MS);

    // Plugin-Init ist nicht boot-kritisch: Fehler loggen, nie den Mount blockieren.
    const safely = (label, fn) => {
      try {
        fn();
      } catch (e) {
        logError(e, 'cordova-boot-init-failed', { step: label });
      }
    };

    document.addEventListener(
      'deviceready',
      () => {
        clearTimeout(watchdog);
        // Der Listener bleibt nach dem Watchdog bewusst scharf: ob deviceready
        // zwei Sekunden später doch noch kommt oder nie, ist die Frage, die
        // über den Fix entscheidet — Timeout zu knapp vs. hängendes Plugin.
        // Die Message bleibt konstant (Zahlen nur in den Extras), damit Sentry
        // das zu einer Gruppe zusammenfasst statt zu einer pro Dauer.
        if (watchdogFired) {
          logError(
            new Error('deviceready fired after the watchdog had already rendered'),
            'cordova-boot-late-deviceready',
            { elapsedMs: Date.now() - bootStartedAt, ...cordovaBootSnapshot() },
          );
        }
        settle();

        // status bar
        safely('statusbar', () => {
          window.StatusBar.overlaysWebView(false);
          window.StatusBar.styleLightContent();

          if (window.cordova.platformId === 'android') {
            window.StatusBar.backgroundColorByHexString('#000000');
          } else if (window.cordova.platformId === 'ios') {
            window.StatusBar.backgroundColorByName('black');
          }
        });

        // handle external links
        safely('inappbrowser', () => {
          window.open = window.cordova.InAppBrowser.open;
        });

        // init deeplink
        safely('deeplink', () => initDeeplink());

        // init one signal
        safely('onesignal', () => oneSignalInit());

        // native firebase analytics: the plugin namespace only exists from now
        // on — re-apply the stored consent decision so setConsent/setEnabled
        // reach the native SDK (boot-time initConsent ran before deviceready).
        safely('firebase-analytics', () => {
          const consent = getConsent();
          if (consent) applyConsent(consent);
        });

        safely('app-version', () => {
          window.cordova.getAppVersion().then(function(version) {
            window.currentAppVersion = version;
            checkNativeAppUpdate();
          });
        });
      },
      false,
    );

    document.addEventListener(
      'resume',
      async () => {
        checkNativeAppUpdate();
        // The ATT status can change in the iOS settings while the app is in the background.
        syncConsentTags();

        // Re-validate auth token after app resumes from background.
        // iOS can suspend the app for hours, causing access tokens to expire.
        // The SDK's built-in refresh handles getting a new access token if the
        // refresh token is still valid. If it's not, authInfo will correctly
        // mark the user as unauthenticated.
        try {
          await dispatch(authInfo());
          const { isAuthenticated } = dispatch((_, getState) => getState().auth);
          if (isAuthenticated) {
            dispatch(fetchCurrentUser());
          }
        } catch (e) {
          logError(e, 'resume-auth-refresh-failed');
        }
      },
    );

    document.addEventListener('backbutton', handleBackButton, false);

    window.addEventListener(
      'load',
      function() {
        // open external links in system browser
        document.addEventListener('click', (e) => {
          if (e.target) {
            let link;
            if (e.target.nodeName === 'a') {
              link = e.target;
            } else {
              link = e.target.closest('a');
            }
            if (link && (link.target === '_blank' || link.target === '_system')) {
              e.preventDefault();
              window.open(link.href, '_system');
            }
          }
        });
      },
      false,
    );
  });
};

export const openUrlInIOS = (url) => {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.click();
};